import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useApi } from '../contexts/ApiContext';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorMessage from '../components/common/ErrorMessage';
import PDFViewer from '../components/document/PDFViewer';

const DocumentPreviewPage: React.FC = () => {
  const { documentId } = useParams<{ documentId: string }>();
  const [searchParams] = useSearchParams();
  const [pageCount, setPageCount] = useState<number | null>(null);
  const [docName, setDocName] = useState('');
  const [error, setError] = useState('');

  const { documents } = useApi();

  const permissions = {
    canView: searchParams.get('canView') !== 'false',
    canDownload: searchParams.get('canDownload') === 'true',
    canPrint: searchParams.get('canPrint') === 'true',
    watermark: searchParams.get('watermark') === 'true',
    allowedPages: searchParams.get('allowedPages')
      ? searchParams.get('allowedPages')!.split(',').map(Number)
      : undefined,
    deniedPages: searchParams.get('deniedPages')
      ? searchParams.get('deniedPages')!.split(',').map(Number)
      : undefined,
  };

  useEffect(() => {
    if (!documentId) return;
    documents.getDocumentMetadata(documentId)
      .then((data: any) => {
        setPageCount(data.page_count);
        setDocName(data.name);
      })
      .catch(() => setError('Failed to load document'));
  }, [documentId]);

  if (error) return <ErrorMessage message={error} />;
  if (pageCount === null) return <LoadingSpinner message="Loading preview..." />;

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <div style={{ background: '#fff', borderBottom: '1px solid #e9ecef', padding: '10px 20px', flexShrink: 0 }}>
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <strong>{docName}</strong>
            <span className="text-muted ms-2" style={{ fontSize: '13px' }}>— Preview as recipient</span>
          </div>
          <div style={{ fontSize: '13px' }}>
            {permissions.canView && <span className="badge bg-success ms-1">View</span>}
            {permissions.canDownload && <span className="badge bg-primary ms-1">Download</span>}
            {permissions.canPrint && <span className="badge bg-secondary ms-1">Print</span>}
            {permissions.watermark && <span className="badge bg-warning text-dark ms-1">Watermark</span>}
            {permissions.allowedPages && <span className="badge bg-info ms-1">Pages: {permissions.allowedPages.join(',')}</span>}
            {permissions.deniedPages && <span className="badge bg-danger ms-1">Denied: {permissions.deniedPages.join(',')}</span>}
          </div>
        </div>
      </div>
      <div style={{ flex: 1, overflow: 'hidden' }}>
        <PDFViewer
          documentId={documentId!}
          pageCount={pageCount}
          isAnonymous={false}
          permissions={permissions}
        />
      </div>
    </div>
  );
};

export default DocumentPreviewPage;
